import type { ValidationError } from '@/lib/graphql/generated/graphql';
import type { RawDealItemForValidation } from '@/lib/workers/validation.worker';
import { ValidationRuleId } from '@/lib/graphql/generated/graphql';
import ValidationWorker from '@/lib/workers/validation.worker?worker&inline';
import {
  backendValidatedProductIdsAtom,
  localValidationErrorsAtom,
} from '@/states/products.state';
import type { ValidationErrorData } from '@appchoose/deal-validation-core';
import { getDefaultStore } from 'jotai';

function toValidationError(e: ValidationErrorData): ValidationError {
  return {
    __typename: 'ValidationError',
    id: `local_${e.dealItemId}_${e.ruleId}`,
    ruleId: e.ruleId as ValidationRuleId,
    message: e.message,
    severity: e.severity,
    dealItemId: e.dealItemId,
    createdAt: e.createdAt,
    fixSuggestion: null,
    ruleName: null,
  };
}

let worker: Worker | null = null;
let debounce: ReturnType<typeof setTimeout> | null = null;

function getWorker(): Worker {
  if (worker) return worker;

  worker = new ValidationWorker();
  worker.onmessage = (
    event: MessageEvent<{
      type: string;
      errors: Record<string, ValidationErrorData[]>;
    }>
  ) => {
    if (event.data.type !== 'VALIDATION_RESULT') return;
    const mapped = new Map<string, ValidationError[]>(
      Object.entries(event.data.errors).map(([id, errors]) => [
        id,
        errors.map(toValidationError),
      ])
    );
    getDefaultStore().set(localValidationErrorsAtom, mapped);
  };

  return worker;
}

export function validate(items: RawDealItemForValidation[]): void {
  const w = getWorker();
  if (debounce) clearTimeout(debounce);
  debounce = setTimeout(() => {
    w.postMessage({ type: 'VALIDATE', items });
  }, 150);
}

export function markBackendConfirmed(ids: string[]): void {
  getDefaultStore().set(backendValidatedProductIdsAtom, (prev) => {
    const next = new Set(prev);
    ids.forEach((id) => next.add(id));
    return next;
  });
}

function terminateWorker(): void {
  if (debounce) clearTimeout(debounce);
  debounce = null;
  worker?.terminate();
  worker = null;
}

/**
 * Terminates the current worker and drops any pending debounce/postMessage.
 * Call this when switching to a different deal so validation results in
 * flight for the previous deal can't land in the new deal's atoms.
 */
export function resetValidationWorker(): void {
  terminateWorker();
}

export function __resetValidationServiceForTests(): void {
  terminateWorker();
}
